import { useMutation } from '@tanstack/react-query';
import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { Button, Spinner } from 'reactstrap';
import profile_placeholder from "assets/images/profile_placeholder.png";
import cover_placeholder from "assets/images/cover_placeholder.png";
import { useFormik } from 'formik';
import * as Yup from 'yup';
// Import social media and 2FA icons
import googleIcon from 'assets/images/icons/google.svg';
import facebookIcon from 'assets/images/icons/facebook.svg';
import appleIcon from 'assets/images/icons/apple.svg';
import smsIcon from 'assets/images/icons/sms-notification.svg';
import authenticatorIcon from 'assets/images/icons/authenticator.svg';
import { updateUserDetails, userDetailById, userImageUpload } from 'endpoints/users/usersListingspage.action';

interface UserEditModalProps {
    isOpen: boolean;
    toggle: () => void;
    user: string;
    refetch?: () => void; // Optional refetch function to refresh data after editing
}

const UserEditModal = ({ isOpen, toggle, user, refetch }: UserEditModalProps) => {
    const [profileImage, setProfileImage] = useState<string>('');
    const [coverImage, setCoverImage] = useState<string>('');
    // Track which image type is currently uploading so we can show per-button loader
    const [uploadingType, setUploadingType] = useState<'profile' | 'cover' | null>(null);
    // Local flag to show loader immediately when modal opens
    const [loadingDetails, setLoadingDetails] = useState<boolean>(true);
    // Image loading states
    const [profileImageLoading, setProfileImageLoading] = useState(true);
    const [profileImageError, setProfileImageError] = useState(false);
    const [coverImageLoading, setCoverImageLoading] = useState(true);
    const [coverImageError, setCoverImageError] = useState(false);
    // Track the actual current status (what's saved on server) vs form status (what user selected)
    const [actualStatus, setActualStatus] = useState<'active' | 'suspended'>('active');
    // State for social accounts and 2FA data
    const [socialAccounts, setSocialAccounts] = useState({
        is_google_connected: false,
        is_facebook_connected: false,
        is_apple_connected: false
    });
    const [twoFactorAuth, setTwoFactorAuth] = useState({
        text_otp_2fa: false,
        auth_app_2fa: false
    });

    // Refs for hidden file inputs
    const profileInputRef = useRef<HTMLInputElement | null>(null);
    const coverInputRef = useRef<HTMLInputElement | null>(null);

    // Unified image upload mutation (handles profile and cover)
    const imageUploadMutation = useMutation({
        mutationFn: ({ id, formData }: { id: string; formData: FormData; type?: 'profile' | 'cover' }) => userImageUpload(id, formData),
    onSuccess: (data, variables: { id: string; formData: FormData; type?: 'profile' | 'cover' }) => {
            // variables is the object passed to mutate: { id, formData, type }
            const type = (variables as any)?.type;
            if (data?.status_code === 200) {
                // backend may return different fields depending on type
                if (type === 'profile') {
                    const newProfileImage = data?.data?.profile_image || data?.data?.image_url;
                    if (newProfileImage) setProfileImage(newProfileImage);
                    toast.success('Profile image updated successfully!', { position: 'top-right', autoClose: 3000 });
                } else if (type === 'cover') {
                    const newCoverImage = data?.data?.cover_photo || data?.data?.image_url || data?.data?.cover;
                    if (newCoverImage) setCoverImage(newCoverImage);
                    toast.success('Cover image updated successfully!', { position: 'top-right', autoClose: 3000 });
                } else {
                    toast.success('Image uploaded successfully', { position: 'top-right', autoClose: 3000 });
                }

                // clear uploading flag for UI
                setUploadingType(null);
                
                // Refetch user data in BasicInformation component
                if (refetch) {
                    refetch();
                }
            } else {
                toast.error(data?.message || 'Failed to upload image', { position: 'top-right', autoClose: 3000 });
                setUploadingType(null);
            }
        },
    onError: (error: any) => {
            console.error('Image upload error:', error);
            toast.error(error?.message || 'Failed to upload image', { position: 'top-right', autoClose: 3000 });
            setUploadingType(null);
        }
    });

    // Handle file selection for profile/cover
    const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>, type: 'profile' | 'cover') => {
        const file = e.target.files?.[0];
        if (!file) return;
        const formData = new FormData();
        // backend expects the file field name; use 'file' or check API; we'll send 'file'
        formData.append('file', file);
        formData.append('type', type);
    // set uploading type for UI and pass type through variables so onSuccess knows which state to update
    setUploadingType(type);
    imageUploadMutation.mutate({ id: user, formData, type });
        // clear the input so same file can be selected again if needed
        e.currentTarget.value = '';
    };

    // TypeScript interfaces for form values
    interface UserFormValues {
        first_name: string;
        last_name: string;
        email: string;
        phone_no: string;
        street_address: string;
        Account_status: 'active' | 'suspended';
    }

    // Validation Schema
    const validationSchema = Yup.object({
        first_name: Yup.string()
            .min(2, 'First name must be at least 2 characters')
            .max(50, 'First name must be less than 50 characters')
            .matches(
              /^[a-zA-Z]*$/,
              'First name can only contain letters'
            ),
        last_name: Yup.string()
            .min(2, 'Last name must be at least 2 characters')
            .max(50, 'Last name must be less than 50 characters')
            .matches(
              /^[a-zA-Z]*$/,
              'Last name can only contain letters'
            ),
        phone_no: Yup.string()
            .matches(/^[0-9+\-() ]*$/, 'Phone number can only contain digits, spaces, and +-()'),
        street_address: Yup.string().min(5, 'Location must be at least 5 characters'),
        Account_status: Yup.string().oneOf(['active', 'suspended'], 'Invalid status')
    });

    // Update User Mutation
    const updateUserMutation = useMutation({
        mutationFn: ({ id, data }: { id: string; data: UserUpdatePayload }) => updateUserDetails(id, data),
        onSuccess: (data) => {
            if (data?.status_code === 200) {
                toast.success('User details updated successfully');
                // Update the actual status only after successful API call
                setActualStatus(formik.values.Account_status);
                // First close the modal
                // toggle();
                // Then trigger the refetch to update the table data
                if (refetch) {
                    refetch();
                }
            }
        },
        onError: () => {
            toast.error('Error updating inspector details');
        }
    });

    // Get Inspector Details Mutation
    const inspectorDetailMutation = useMutation({
        mutationFn: userDetailById,
        onSuccess: (data) => {
            if (data?.status_code === 200 && data?.data) {
                const userData = data.data;
                setProfileImage(userData.profile_image === 'profile_skipped' ? '' : userData.profile_image);
                setCoverImage(userData.cover_photo === 'cover_skipped' ? '' : userData.cover_photo);

                // Set both actual status and form initial values from API response
                const currentStatus = userData.Account_status || 'active';
                setActualStatus(currentStatus);

                // Set social accounts data
                setSocialAccounts({
                    is_google_connected: userData.is_google_connected || false,
                    is_facebook_connected: userData.is_facebook_connected || false,
                    is_apple_connected: userData.is_apple_connected || false
                });

                // Set 2FA data
                setTwoFactorAuth({
                    text_otp_2fa: userData.text_otp_2fa || false,
                    auth_app_2fa: userData.auth_app_2fa || false
                });

                // Set form initial values from API response
                formik.setValues({
                    first_name: userData.first_name || '',
                    last_name: userData.last_name || '',
                    email: userData.email || '',
                    phone_no: userData.phone_no || '',
                    street_address: userData.street_address || '',
                    Account_status: currentStatus
                });
                setLoadingDetails(false);
            }
        },
        onError: () => {
            toast.error('Error fetching inspector details');
            setLoadingDetails(false);
        }
    });

    // Formik initialization
    // TypeScript interface for API payload
    interface UserUpdatePayload {
        first_name?: string;
        last_name?: string;
        email?: string;
        phone_no?: string;
        street_address?: string;
        Account_status?: string;
    }

    // Handle form submit with proper typing
    const handleSubmit = (values: UserFormValues) => {
        // Only include fields that have values, excluding email
        const payload: UserUpdatePayload = {
            ...(values.first_name && { first_name: values.first_name }),
            ...(values.last_name && { last_name: values.last_name }),
            ...(values.phone_no && { phone_no: values.phone_no }),
            ...(values.street_address && { street_address: values.street_address }),
            ...(values.Account_status && { Account_status: values.Account_status })
        };
        updateUserMutation.mutate({ id: user, data: payload });
    };

    const formik = useFormik<UserFormValues>({
        initialValues: {
            first_name: '',
            last_name: '',
            email: '',
            phone_no: '',
            street_address: '',
            Account_status: 'active'
        },
        validationSchema,
        onSubmit: handleSubmit,
        enableReinitialize: true  // This will allow form to update when initial values change
    });
    useEffect(() => {
        if (isOpen) {
            if (user) {
                inspectorDetailMutation.mutate(user);
            } else {
                toast.error('User ID is required to fetch inspector details');
                setLoadingDetails(false);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, user]);

    // Close modal and clear all form/image values
    const closeAndClear = () => {
        // clear images and uploading state
        setProfileImage('');
        setCoverImage('');
        setUploadingType(null);
        
        // Reset actual status
        setActualStatus('active');

        // Reset social accounts and 2FA data
        setSocialAccounts({
            is_google_connected: false,
            is_facebook_connected: false,
            is_apple_connected: false
        });
        setTwoFactorAuth({
            text_otp_2fa: false,
            auth_app_2fa: false
        });

        // clear hidden file inputs if present
        if (profileInputRef.current) profileInputRef.current.value = '';
        if (coverInputRef.current) coverInputRef.current.value = '';

        // clear form values
        formik.setValues({
            first_name: '',
            last_name: '',
            email: '',
            phone_no: '',
            street_address: '',
            Account_status: 'active'
        });

        // finally close the modal
        toggle();
    };

    // Reset image loading states when images change
    useEffect(() => {
        if (profileImage) {
            setProfileImageLoading(true);
            setProfileImageError(false);
        }
    }, [profileImage]);

    useEffect(() => {
        if (coverImage) {
            setCoverImageLoading(true);
            setCoverImageError(false);
        }
    }, [coverImage]);

    // Handle image load events for profile image
    const handleProfileImageLoad = () => {
        setProfileImageLoading(false);
        setProfileImageError(false);
    };

    const handleProfileImageError = () => {
        setProfileImageLoading(false);
        setProfileImageError(true);
    };

    // Handle image load events for cover image
    const handleCoverImageLoad = () => {
        setCoverImageLoading(false);
        setCoverImageError(false);
    };

    const handleCoverImageError = () => {
        setCoverImageLoading(false);
        setCoverImageError(true);
    };

    if (!isOpen) return null;

    return (
        <>
            {/* Modal Backdrop */}
            <div
                className="modal-backdrop fade show"
                style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    zIndex: 1040
                }}
                onClick={closeAndClear}
            ></div>

            {/* Modal */}
            <div
                className="modal fade show d-block"
                style={{ zIndex: 1050 }}
                tabIndex={-1}
            >
                <div className="modal-dialog modal-lg modal-dialog-centered">
                    <div className="modal-content">
                        {/* Modal Header */}
                        <div className="modal-header border-bottom-0 pb-0">
                            <h5 className="modal-title fw-semibold">Edit User</h5>
                            <button
                                type="button"
                                className="btn-close"
                                onClick={closeAndClear}
                                aria-label="Close"
                            ></button>
                        </div>

                        {/* Modal Body */}
                        {(loadingDetails || inspectorDetailMutation.isPending) ? (
                            <div className="d-flex justify-content-center align-items-center" style={{ height: '100vh' }}>
                                <div className="spinner-border text-primary" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                </div>
                            </div>
                        ) : (
                            <>
                                <div className="modal-body pt-2 pb-2 px-3">
                                    {/* divider */}
                                    {/* <hr className="my-0" /> */}
                                    {/* Image Upload Area */}
                                    <div className="position-relative mb-5">
                                        {/* Cover Photo Area */}
                                        <div className="position-relative">
                                            {/* Cover Image Loader */}
                                            {coverImageLoading && !coverImageError && (
                                                <div style={{ 
                                                    position: 'absolute', 
                                                    top: 0, 
                                                    left: 0, 
                                                    right: 0, 
                                                    bottom: 0, 
                                                    display: 'flex', 
                                                    alignItems: 'center', 
                                                    justifyContent: 'center', 
                                                    borderRadius: '12px', 
                                                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                                    zIndex: 1
                                                }}>
                                                    <Spinner color="primary" />
                                                </div>
                                            )}
                                            <img
                                                src={coverImageError ? cover_placeholder : (coverImage || cover_placeholder)}
                                                alt="Cover"
                                                className='w-100'
                                                style={{
                                                    height: '250px',
                                                    objectFit: 'cover',
                                                    borderRadius: '12px',
                                                    overflow: 'hidden',
                                                    opacity: coverImageLoading ? 0.3 : 1,
                                                    transition: 'opacity 0.3s ease'
                                                }}
                                                onLoad={handleCoverImageLoad}
                                                onError={handleCoverImageError}
                                            />
                                            <Button
                                                size="sm"
                                                color='light'
                                                className="position-absolute bg-white"
                                                style={{
                                                    top: "10px",
                                                    right: "10px",
                                                    borderRadius: "50%",
                                                    width: "32px",
                                                    height: "32px",
                                                    padding: "0",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                }}
                                                onClick={() => coverInputRef.current?.click()}
                                                disabled={uploadingType === 'cover'}
                                            >
                                                {uploadingType === 'cover' ? (
                                                    <div className="spinner-border spinner-border-sm" role="status">
                                                        <span className="visually-hidden">Loading...</span>
                                                    </div>
                                                ) : (
                                                    <i className="ri-pencil-fill fs-5"></i>
                                                )}
                                            </Button>
                                        </div>

                                        {/* Profile Photo */}
                                        <div
                                            className="text-center"
                                            style={{
                                                position: "absolute",
                                                bottom: "-65px",
                                                left: 0,
                                                right: 0,
                                                zIndex: 2,
                                            }}
                                        >
                                            <div className="position-relative d-inline-block">
                                                <div className="rounded-circle bg-white d-flex align-items-center justify-content-center" style={{ position: 'relative' }}>
                                                    {/* Profile Image Loader */}
                                                    {profileImageLoading && !profileImageError && (
                                                        <div style={{ 
                                                            position: 'absolute', 
                                                            top: 0, 
                                                            left: 0, 
                                                            right: 0, 
                                                            bottom: 0, 
                                                            display: 'flex', 
                                                            alignItems: 'center', 
                                                            justifyContent: 'center', 
                                                            borderRadius: '50%', 
                                                            backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                                            zIndex: 1
                                                        }}>
                                                            <Spinner size="sm" color="primary" />
                                                        </div>
                                                    )}
                                                    <img
                                                        src={profileImageError ? profile_placeholder : (profileImage || profile_placeholder)}
                                                        alt="Profile"
                                                        style={{
                                                            width: 140,
                                                            height: 140,
                                                            borderRadius: "50%",
                                                            objectFit: "cover",
                                                            border: "4px solid #fff",
                                                            boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                                                            opacity: profileImageLoading ? 0.3 : 1,
                                                            transition: 'opacity 0.3s ease'
                                                        }}
                                                        onLoad={handleProfileImageLoad}
                                                        onError={handleProfileImageError}
                                                    />
                                                    {/* style={{
                                                        width: 140,
                                                        height: 140,
                                                        border: "5px solid #fff",
                                                        boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                                                    }} */}
                                                </div>
                                                <Button
                                                    color="light"
                                                    size="sm"
                                                    className="position-absolute"
                                                    style={{
                                                        bottom: "20px",
                                                        right: "0px",
                                                        borderRadius: "50%",
                                                        width: "32px",
                                                        height: "32px",
                                                        padding: "0",
                                                        display: "flex",
                                                        alignItems: "center",
                                                        justifyContent: "center",
                                                    }}
                                                    onClick={() => profileInputRef.current?.click()}
                                                    disabled={uploadingType === 'profile'}
                                                >
                                                    {uploadingType === 'profile' ? (
                                                        <div className="spinner-border spinner-border-sm" role="status">
                                                            <span className="visually-hidden">Loading...</span>
                                                        </div>
                                                    ) : (
                                                        <i className="ri-pencil-fill fs-5"></i>
                                                    )}
                                                </Button>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Hidden file inputs for profile/cover upload */}
                                    <input
                                        type="file"
                                        accept="image/*"
                                        ref={profileInputRef}
                                        style={{ display: 'none' }}
                                        onChange={(e) => handleImageSelect(e, 'profile')}
                                    />
                                    <input
                                        type="file"
                                        accept="image/*"
                                        ref={coverInputRef}
                                        style={{ display: 'none' }}
                                        onChange={(e) => handleImageSelect(e, 'cover')}
                                    />

                                    {/* Basic Info */}
                                    <div className="fw-semibold mb-0" style={{ fontSize: 17 }}>Basic Info</div>
                                    {/* divider */}
                                    <hr className="mb-4" />
                                    <div className="row gy-3">
                                        <div className="col-md-6">
                                            <div className="mb-2">
                                                <label className="form-label mb-1">First Name</label>
                                                <input
                                                    type="text"
                                                    className={`form-control ${formik.touched.first_name && formik.errors.first_name ? 'is-invalid' : ''}`}
                                                    placeholder='Enter first name'
                                                    {...formik.getFieldProps('first_name')}
                                                />
                                                {formik.touched.first_name && formik.errors.first_name && (
                                                    <div className="invalid-feedback">{formik.errors.first_name}</div>
                                                )}
                                            </div>
                                        </div>
                                        <div className="col-md-6">
                                            <div className="mb-2">
                                                <label className="form-label mb-1">Last Name</label>
                                                <input
                                                    type="text"
                                                    className={`form-control ${formik.touched.last_name && formik.errors.last_name ? 'is-invalid' : ''}`}
                                                    placeholder='Enter last name'
                                                    {...formik.getFieldProps('last_name')}
                                                />
                                                {formik.touched.last_name && formik.errors.last_name && (
                                                    <div className="invalid-feedback">{formik.errors.last_name}</div>
                                                )}
                                            </div>
                                        </div>
                                        <div className="col-md-6">
                                            <div className="mb-2">
                                                <label className="form-label mb-1">Email</label>
                                                <input
                                                    type="email"
                                                    className="form-control bg-light text-muted"
                                                    value={formik.values.email}
                                                    readOnly
                                                    style={{ cursor: 'not-allowed' }}
                                                />
                                                <small className="text-muted mt-1 d-block" style={{ fontSize: '12px' }}>
                                                    <i className="ri-information-line align-middle me-1"></i>
                                                    Email address cannot be updated
                                                </small>
                                            </div>
                                        </div>
                                        <div className="col-md-6">
                                            <div className="mb-2">
                                                <label className="form-label mb-1">Phone Number</label>
                                                <input
                                                    type="text"
                                                    className={`form-control ${formik.touched.phone_no && formik.errors.phone_no ? 'is-invalid' : ''}`}
                                                    placeholder='Enter phone number'
                                                    {...formik.getFieldProps('phone_no')}
                                                />
                                                {formik.touched.phone_no && formik.errors.phone_no && (
                                                    <div className="invalid-feedback">{formik.errors.phone_no}</div>
                                                )}
                                            </div>
                                        </div>
                                        <div className="col-md-12">
                                            <div className="mb-2">
                                                <label className="form-label mb-1">Location</label>
                                                <input
                                                    type="text"
                                                    className={`form-control ${formik.touched.street_address && formik.errors.street_address ? 'is-invalid' : ''}`}
                                                    placeholder='Enter location'
                                                    {...formik.getFieldProps('street_address')}
                                                />
                                                {formik.touched.street_address && formik.errors.street_address && (
                                                    <div className="invalid-feedback">{formik.errors.street_address}</div>
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Social Accounts */}
                                    <div className="fw-semibold mt-4 mb-0" style={{ fontSize: 17 }}>Social Accounts</div>
                                    <hr className="mb-4" />
                                    <div className="row align-items-center mb-3">
                                        <div className="col-12">
                                            <div className="d-flex gap-3 align-items-center" style={{ cursor: 'not-allowed' }}>
                                                <div className="d-flex align-items-center gap-2">
                                                    <img 
                                                        src={googleIcon}
                                                        alt="Google"
                                                        style={{ 
                                                            width: '24px', 
                                                            height: '24px',
                                                            opacity: socialAccounts.is_google_connected ? 1 : 0.5,
                                                            filter: socialAccounts.is_google_connected ? 'none' : 'grayscale(100%)',
                                                            cursor: 'not-allowed'
                                                        }}
                                                        title={`Google ${socialAccounts.is_google_connected ? 'Connected' : 'Not Connected'}`}
                                                    />
                                                    <span className="text-muted" style={{ fontSize: '14px', cursor: 'not-allowed' }}>
                                                        Google {socialAccounts.is_google_connected ? 'Connected' : 'Not Connected'}
                                                    </span>
                                                </div>
                                                <div className="d-flex align-items-center gap-2">
                                                    <img 
                                                        src={facebookIcon}
                                                        alt="Facebook"
                                                        style={{ 
                                                            width: '24px', 
                                                            height: '24px',
                                                            opacity: socialAccounts.is_facebook_connected ? 1 : 0.5,
                                                            filter: socialAccounts.is_facebook_connected ? 'none' : 'grayscale(100%)',
                                                            cursor: 'not-allowed'
                                                        }}
                                                        title={`Facebook ${socialAccounts.is_facebook_connected ? 'Connected' : 'Not Connected'}`}
                                                    />
                                                    <span className="text-muted" style={{ fontSize: '14px', cursor: 'not-allowed' }}>
                                                        Facebook {socialAccounts.is_facebook_connected ? 'Connected' : 'Not Connected'}
                                                    </span>
                                                </div>
                                                <div className="d-flex align-items-center gap-2">
                                                    <img 
                                                        src={appleIcon}
                                                        alt="Apple"
                                                        style={{ 
                                                            width: '24px', 
                                                            height: '24px',
                                                            opacity: socialAccounts.is_apple_connected ? 1 : 0.5,
                                                            filter: socialAccounts.is_apple_connected ? 'none' : 'grayscale(100%)',
                                                            cursor: 'not-allowed'
                                                        }}
                                                        title={`Apple ${socialAccounts.is_apple_connected ? 'Connected' : 'Not Connected'}`}
                                                    />
                                                    <span className="text-muted" style={{ fontSize: '14px', cursor: 'not-allowed' }}>
                                                        Apple {socialAccounts.is_apple_connected ? 'Connected' : 'Not Connected'}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* 2FA Setup */}
                                    <div className="fw-semibold mt-4 mb-0" style={{ fontSize: 17 }}>2FA Setup</div>
                                    <hr className="mb-4" />
                                    <div className="row align-items-center mb-3">
                                        <div className="col-12">
                                            <div className="d-flex gap-3 align-items-center" style={{ cursor: 'not-allowed' }}>
                                                <div className="d-flex align-items-center gap-2">
                                                    <img 
                                                        src={smsIcon}
                                                        alt="SMS"
                                                        style={{ 
                                                            width: '24px', 
                                                            height: '24px',
                                                            opacity: twoFactorAuth.text_otp_2fa ? 1 : 0.5,
                                                            filter: twoFactorAuth.text_otp_2fa ? 'none' : 'grayscale(100%)',
                                                            cursor: 'not-allowed'
                                                        }}
                                                        title={`SMS 2FA ${twoFactorAuth.text_otp_2fa ? 'Enabled' : 'Disabled'}`}
                                                    />
                                                    <span className="text-muted" style={{ fontSize: '14px', cursor: 'not-allowed' }}>
                                                        SMS 2FA {twoFactorAuth.text_otp_2fa ? 'Enabled' : 'Disabled'}
                                                    </span>
                                                </div>
                                                <div className="d-flex align-items-center gap-2">
                                                    <img 
                                                        src={authenticatorIcon}
                                                        alt="Authenticator"
                                                        style={{ 
                                                            width: '24px', 
                                                            height: '24px',
                                                            opacity: twoFactorAuth.auth_app_2fa ? 1 : 0.5,
                                                            filter: twoFactorAuth.auth_app_2fa ? 'none' : 'grayscale(100%)',
                                                            cursor: 'not-allowed'
                                                        }}
                                                        title={`Auth App 2FA ${twoFactorAuth.auth_app_2fa ? 'Enabled' : 'Disabled'}`}
                                                    />
                                                    <span className="text-muted" style={{ fontSize: '14px', cursor: 'not-allowed' }}>
                                                        Auth App 2FA {twoFactorAuth.auth_app_2fa ? 'Enabled' : 'Disabled'}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Builder Status */}
                                    <div className='d-flex justify-content-between align-items-center'>
                                        <div className="fw-semibold mt-4 mb-2" style={{ fontSize: 17 }}>Account Status</div>
                                        <div className={`badge rounded-pill mt-4 ${actualStatus === 'active' ? 'bg-success' : 'bg-danger'}`}>
                                            {actualStatus === 'active' ? 'Active' : 'Suspended'}
                                        </div>
                                    </div>
                                    <hr className="mb-4" />
                                    <div className="row align-items-center mb-3">
                                        <div className="col-12">
                                            <div className="mb-0">
                                                <label className="form-label mb-1">Change Status</label>
                                                <select
                                                    className={`form-select ${formik.touched.Account_status && formik.errors.Account_status ? 'is-invalid' : ''}`}
                                                    style={{
                                                        fontSize: '14px',
                                                        height: '38px'
                                                    }}
                                                    {...formik.getFieldProps('Account_status')}
                                                >
                                                    <option value="active">Active</option>
                                                    <option value="suspended">Suspended</option>
                                                </select>
                                                {formik.touched.Account_status && formik.errors.Account_status && (
                                                    <div className="invalid-feedback">{formik.errors.Account_status}</div>
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Security */}
                                    {/* <div className="fw-semibold mt-4 mb-0" style={{ fontSize: 17 }}>Security</div>
                                    <hr className="mb-4" />
                                    <div className="d-flex flex-row justify-content-end align-items-end gap-3">
                                        <div style={{ flex: 1 }}>
                                            <label className="form-label mb-1" style={{ fontSize: '14px' }}>Update Password</label>
                                            <input
                                                type="password"
                                                className="form-control mb-0"
                                                placeholder='Enter new password'
                                                style={{
                                                    background: '#fff',
                                                    fontSize: '14px',
                                                    height: '38px'
                                                }}
                                            />
                                        </div>
                                        <div>
                                            <Button
                                                color="success"
                                                type="button"
                                                onClick={() => { }}
                                                style={{ width: "110px", height: "38px" }}
                                            >
                                                Update
                                            </Button>
                                        </div>
                                    </div> */}
                                </div>

                                {/* Modal Footer */}
                                <div className="modal-footer border-top-0 pt-2 pb-3 px-3">
                                    <div className="d-flex flex-row justify-content-end align-items-end gap-3">
                                        <Button
                                            color="danger"
                                            type="button"
                                            style={{ width: "110px" }}
                                            onClick={closeAndClear}
                                            disabled={updateUserMutation.isPending}
                                        >
                                            Cancel
                                        </Button>
                                        <Button
                                            color="success"
                                            type="button"
                                            style={{ width: "110px" }}
                                            onClick={() => formik.handleSubmit()}
                                            disabled={updateUserMutation.isPending || (!formik.values.first_name && !formik.values.last_name && !formik.values.email && !formik.values.phone_no && !formik.values.street_address && !formik.values.Account_status)}
                                        >
                                            {updateUserMutation.isPending ? (
                                                <div className="spinner-border spinner-border-sm" role="status">
                                                    <span className="visually-hidden">Loading...</span>
                                                </div>
                                            ) : (
                                                'Save'
                                            )}
                                        </Button>
                                    </div>
                                </div>
                            </>)}
                    </div>
                </div>
            </div>
        </>
    );
};

export default UserEditModal;